import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Search, Inbox, Send, FileEdit, AlertTriangle, Trash2,
  Star, Paperclip, X, Reply, ReplyAll, Forward, Mail, MailOpen, ImageOff,
  ChevronLeft, ChevronRight, Plus, Download, CornerUpLeft,
} from "lucide-react";

/*
  The admin mailbox.

  Nothing here is stored by PawLinx. Every list, every message body and every
  search goes straight to the mail server, so what the admin sees is exactly
  what is in the mailbox right now, and closing this screen leaves nothing
  behind.

  Two safety rules are baked into this file:
  1. A message body is never rendered into the page. It goes into an iframe
     that is not allowed to run scripts, so a hostile email cannot touch the
     admin session even if the server side cleaner ever missed something.
  2. Remote images stay blocked until the admin asks for them, because loading
     one tells the sender the address is live and being read.
*/

const FOLDER_ICONS = {
  inbox: Inbox,
  sent: Send,
  drafts: FileEdit,
  junk: AlertTriangle,
  trash: Trash2,
};

const PER_PAGE = 40;

/* Multipart has to bypass the shared api client, because that client sends
   application/json by default and the browser has to be the one that sets the
   multipart boundary. Same origin and the same cookie, just without the
   default header. */
const postForm = (path, fd) =>
  axios.post("/api" + path, fd, { withCredentials: true });

const cx = (...a) => a.filter(Boolean).join(" ");

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const textToHtml = (t) =>
  esc(t)
    .split(/\n{2,}/)
    .map((p) => "<p>" + p.replace(/\n/g, "<br>") + "</p>")
    .join("");

/* Pull the typed part back out of a saved draft. The server marks it with
   data-pawlinx-body, so this never has to guess where the signature begins.
   DOMParser builds a detached document, it does not run anything. */
const htmlToText = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    const node = doc.querySelector("[data-pawlinx-body]") || doc.body;
    if (!node) return "";
    node.querySelectorAll("br").forEach((b) => b.replaceWith("\n"));
    node.querySelectorAll("p, div, li, tr").forEach((b) => b.append("\n"));
    return (node.textContent || "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  } catch (e) {
    return "";
  }
};

const who = (list) => {
  const a = (list || [])[0];
  if (!a) return "";
  return a.name || a.email || "";
};

const whoFull = (list) =>
  (list || []).map((a) => (a.name ? a.name + " <" + a.email + ">" : a.email)).join(", ");

const addrLine = (list) => (list || []).map((a) => a.email).filter(Boolean).join(", ");

const fmtDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (d.getFullYear() === now.getFullYear())
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  return d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
};

const fmtFull = (ts) =>
  ts ? new Date(ts * 1000).toLocaleString([], {
    weekday: "short", year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  }) : "";

const fmtSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(0) + " KB";
  return (n / 1024 / 1024).toFixed(1) + " MB";
};

const frameDoc = (html) =>
  '<!doctype html><html><head><meta charset="utf-8">' +
  "<style>" +
  "body{margin:0;padding:18px;font:14px/1.65 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0E2C4F;background:#ffffff;word-wrap:break-word;overflow-wrap:anywhere}" +
  "img{max-width:100%;height:auto}" +
  "blockquote{margin:14px 0;padding-left:14px;border-left:3px solid #E5DECE;color:#556275}" +
  "table{max-width:100%}pre{white-space:pre-wrap}a{color:#0F62D6}" +
  "</style></head><body>" +
  (html || "<p style=\"color:#556275\">This message has no readable content.</p>") +
  "</body></html>";

export default function AdminEmail() {
  const [accounts, setAccounts] = useState([]);
  const [folders, setFolders] = useState([]);
  const [account, setAccount] = useState("all");
  const [folder, setFolder] = useState("inbox");
  const [only, setOnly] = useState("all");
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listErrors, setListErrors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openId, setOpenId] = useState("");
  const [full, setFull] = useState(null);
  const [fullLoading, setFullLoading] = useState(false);
  const [showImages, setShowImages] = useState(false);

  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const [compose, setCompose] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const currentRow = useMemo(() => rows.find((r) => r.id === openId) || null, [rows, openId]);

  /* ---------- loading ---------- */

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/mail/folders");
      setFolders(data.folders || []);
    } catch (e) {
      /* the message list surfaces the real error, no need to shout twice */
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/mail/messages", {
        params: { account, folder, page, per_page: PER_PAGE, q, only },
      });
      setRows(data.messages || []);
      setTotal(data.total || 0);
      setListErrors(data.errors || []);
    } catch (e) {
      setRows([]);
      setTotal(0);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [account, folder, page, q, only]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/mail/accounts");
        setAccounts(data.accounts || []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setPage(1); setPicked([]); setOpenId(""); setFull(null); }, [account, folder, only, q]);

  const openMessage = async (row, wantImages) => {
    setOpenId(row.id);
    setFullLoading(true);
    setShowImages(!!wantImages);
    try {
      const { data } = await api.get("/admin/mail/message", {
        params: { account: row.account, folder: row.folder, uid: row.uid, images: wantImages ? 1 : 0 },
      });
      setFull(data);
    } catch (e) {
      setFull(null);
      toast.error(formatApiError(e));
    } finally {
      setFullLoading(false);
    }
  };

  /* ---------- actions ---------- */

  const groupPicked = () => {
    /* the combined inbox can hold rows from both mailboxes, and IMAP work has
       to be done one mailbox and one folder at a time */
    const groups = {};
    picked.forEach((id) => {
      const r = rows.find((x) => x.id === id);
      if (!r) return;
      const k = r.account + "|" + r.folder;
      (groups[k] = groups[k] || []).push(r.uid);
    });
    return Object.entries(groups).map(([k, uids]) => {
      const [acct, fld] = k.split("|");
      return { account: acct, folder: fld, uids };
    });
  };

  const runOnPicked = async (path, extra, done) => {
    const groups = groupPicked();
    if (!groups.length) { toast.error("Select a message first"); return; }
    setBusy(true);
    try {
      for (const g of groups) {
        const fd = new FormData();
        fd.append("account", g.account);
        fd.append("folder", g.folder);
        fd.append("uids", g.uids.join(","));
        Object.entries(extra || {}).forEach(([k, v]) => fd.append(k, v));
        await postForm(path, fd);
      }
      toast.success(done);
      setPicked([]);
      await Promise.all([loadList(), loadFolders()]);
      if (path === "/admin/mail/trash") { setOpenId(""); setFull(null); }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setOne = async (row, extra, quiet) => {
    const fd = new FormData();
    fd.append("account", row.account);
    fd.append("folder", row.folder);
    fd.append("uids", row.uid);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    try {
      await postForm("/admin/mail/flags", fd);
      await Promise.all([loadList(), loadFolders()]);
    } catch (e) {
      if (!quiet) toast.error(formatApiError(e));
    }
  };

  const moveOne = async (row, to) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("account", row.account);
      fd.append("folder", row.folder);
      fd.append("uids", row.uid);
      fd.append("to_folder", to);
      await postForm("/admin/mail/move", fd);
      toast.success("Moved");
      setOpenId(""); setFull(null);
      await Promise.all([loadList(), loadFolders()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const trashOne = async (row) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("account", row.account);
      fd.append("folder", row.folder);
      fd.append("uids", row.uid);
      await postForm("/admin/mail/trash", fd);
      toast.success("Moved to Trash");
      setOpenId(""); setFull(null);
      await Promise.all([loadList(), loadFolders()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  /* ---------- compose ---------- */

  const startNew = () => setCompose({
    mode: "new",
    account: account === "all" ? (accounts[0] ? accounts[0].key : "support") : account,
    to: "", cc: "", bcc: "", subject: "", body: "",
    replyFolder: "inbox", replyUid: "", showCc: false, files: [],
  });

  const startReply = (all) => {
    if (!full || !currentRow) return;
    const mine = (accounts.find((a) => a.key === currentRow.account) || {}).address || "";
    const to = addrLine(full.reply_to && full.reply_to.length ? full.reply_to : full.from);
    const others = [...(full.to || []), ...(full.cc || [])]
      .map((a) => a.email)
      .filter((e) => e && e.toLowerCase() !== mine.toLowerCase() && !to.toLowerCase().includes(e.toLowerCase()));
    const base = full.subject || "";
    setCompose({
      mode: all ? "reply_all" : "reply",
      account: currentRow.account,
      to,
      cc: all ? others.join(", ") : "",
      bcc: "",
      subject: /^re:/i.test(base) ? base : "Re: " + base,
      body: "",
      replyFolder: currentRow.folder,
      replyUid: currentRow.uid,
      showCc: all && others.length > 0,
      files: [],
    });
  };

  const startForward = () => {
    if (!full || !currentRow) return;
    const base = full.subject || "";
    setCompose({
      mode: "forward",
      account: currentRow.account,
      to: "", cc: "", bcc: "",
      subject: /^fwd:/i.test(base) ? base : "Fwd: " + base,
      body: "",
      replyFolder: currentRow.folder,
      replyUid: currentRow.uid,
      showCc: false,
      files: [],
      keepAttachments: true,
    });
  };

  const startEditDraft = () => {
    if (!full || !currentRow) return;
    setCompose({
      mode: "new",
      account: currentRow.account,
      to: addrLine(full.to),
      cc: addrLine(full.cc),
      bcc: "",
      subject: full.subject || "",
      body: htmlToText(full.html),
      replyFolder: "inbox",
      replyUid: "",
      showCc: !!(full.cc && full.cc.length),
      files: [],
      draftUid: currentRow.uid,
    });
  };

  const composeForm = (c) => {
    const fd = new FormData();
    fd.append("account", c.account);
    fd.append("mode", c.mode);
    fd.append("to", c.to);
    fd.append("cc", c.cc);
    fd.append("bcc", c.bcc);
    fd.append("subject", c.subject);
    fd.append("body_html", textToHtml(c.body));
    fd.append("reply_folder", c.replyFolder || "inbox");
    fd.append("reply_uid", c.replyUid || "");
    (c.files || []).forEach((f) => fd.append("files", f, f.name));
    return fd;
  };

  const doSend = async () => {
    const c = compose;
    if (!c) return;
    if (!c.to.trim() && !c.cc.trim() && !c.bcc.trim()) { toast.error("Add at least one recipient"); return; }
    if (!c.subject.trim()) { toast.error("Add a subject"); return; }
    if (!c.body.trim()) { toast.error("Write a message"); return; }
    setSending(true);
    try {
      const fd = composeForm(c);
      fd.append("include_original_attachments", c.keepAttachments ? "1" : "0");
      fd.append("draft_uid", c.draftUid || "");
      const { data } = await postForm("/admin/mail/send", fd);
      toast.success("Sent to " + (data.sent_to || []).join(", "));
      setCompose(null);
      await Promise.all([loadList(), loadFolders()]);
      if (openId && currentRow) openMessage(currentRow, showImages);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const doDraft = async () => {
    const c = compose;
    if (!c) return;
    setSending(true);
    try {
      const dfd = composeForm(c);
      dfd.append("replace_uid", c.draftUid || "");
      await postForm("/admin/mail/draft", dfd);
      toast.success("Saved to Drafts");
      setCompose(null);
      await Promise.all([loadList(), loadFolders()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  /* ---------- render ---------- */

  const pages = Math.max(1, Math.ceil(total / PER_PAGE));
  const allPicked = rows.length > 0 && picked.length === rows.length;

  const card = "bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl";
  const btn = "flex items-center gap-2 px-3 h-9 rounded-lg bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 font-manrope text-sm transition-colors disabled:opacity-40";
  const field = "w-full h-10 px-3 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 font-manrope text-sm outline-none focus:border-paw-primary";

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10" data-testid="admin-email">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Communication</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Email</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">
            Both PawLinx mailboxes in one place. Read live from the mail server, so nothing is copied or kept here. Anything you send goes out on the PawLinx template.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={startNew} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope text-sm font-semibold transition-colors" data-testid="mail-compose-open">
            <Plus className="w-4 h-4" /> New email
          </button>
          <button onClick={() => { loadList(); loadFolders(); }} className={btn + " h-10 px-4"} data-testid="mail-refresh">
            <RefreshCw className={cx("w-4 h-4", loading && "animate-spin")} /> Refresh
          </button>
        </div>
      </div>

      {listErrors.length > 0 && (
        <div className="mb-4 rounded-xl border border-paw-warning/40 bg-paw-warning/10 px-4 py-3 font-manrope text-sm text-paw-text dark:text-white/80" data-testid="mail-errors">
          {listErrors.map((e, i) => (
            <div key={i}>{e.account}: {e.error}</div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[210px_minmax(0,1fr)] xl:grid-cols-[210px_380px_minmax(0,1fr)] gap-4">

        {/* folders and mailbox filter */}
        <div className={cx(card, "p-3 h-fit")} data-testid="mail-sidebar">
          <p className="font-manrope text-[11px] uppercase tracking-widest text-paw-text-muted dark:text-white/40 px-2 mb-2">Mailbox</p>
          <div className="space-y-1 mb-4">
            {[{ key: "all", label: "Both mailboxes", address: "support and info" }, ...accounts].map((a) => (
              <button
                key={a.key}
                onClick={() => setAccount(a.key)}
                data-testid={"mail-account-" + a.key}
                className={cx(
                  "w-full text-left px-2 py-2 rounded-lg font-manrope text-sm transition-colors",
                  account === a.key
                    ? "bg-paw-primary/10 text-paw-primary dark:bg-paw-primary/20 dark:text-white font-semibold"
                    : "hover:bg-black/[0.04] dark:hover:bg-white/5"
                )}
              >
                <span className="block truncate">{a.key === "all" ? "Both mailboxes" : a.label}</span>
                <span className="block truncate text-[11px] text-paw-text-muted dark:text-white/40">{a.address}</span>
              </button>
            ))}
          </div>

          <p className="font-manrope text-[11px] uppercase tracking-widest text-paw-text-muted dark:text-white/40 px-2 mb-2">Folders</p>
          <div className="space-y-1">
            {folders.map((f) => {
              const Icon = FOLDER_ICONS[f.key] || Inbox;
              return (
                <button
                  key={f.key}
                  onClick={() => setFolder(f.key)}
                  data-testid={"mail-folder-" + f.key}
                  className={cx(
                    "w-full flex items-center gap-2 px-2 py-2 rounded-lg font-manrope text-sm transition-colors",
                    folder === f.key
                      ? "bg-paw-primary/10 text-paw-primary dark:bg-paw-primary/20 dark:text-white font-semibold"
                      : "hover:bg-black/[0.04] dark:hover:bg-white/5"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left truncate">{f.label}</span>
                  {f.unread > 0 && (
                    <span className="px-1.5 rounded-full bg-paw-primary text-white text-[11px] font-semibold">{f.unread}</span>
                  )}
                  {!f.unread && f.total > 0 && (
                    <span className="text-[11px] text-paw-text-muted dark:text-white/30">{f.total}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* message list */}
        <div className={cx(card, "p-3 flex flex-col min-h-[300px]")} data-testid="mail-list">
          <form
            onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }}
            className="relative mb-3"
          >
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-paw-text-muted dark:text-white/30" />
            <input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search this folder"
              data-testid="mail-search"
              className={field + " pl-9 pr-9"}
            />
            {(qInput || q) && (
              <button
                type="button"
                onClick={() => { setQInput(""); setQ(""); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>

          <div className="flex items-center gap-1 mb-3 flex-wrap">
            {[["all", "All"], ["unread", "Unread"], ["starred", "Starred"], ["attachments", "With files"]].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setOnly(k)}
                data-testid={"mail-only-" + k}
                className={cx(
                  "px-2.5 h-7 rounded-full font-manrope text-xs border transition-colors",
                  only === k
                    ? "bg-paw-accent text-white border-paw-accent"
                    : "border-paw-border dark:border-white/10 text-paw-text-muted dark:text-white/50 hover:bg-black/[0.04] dark:hover:bg-white/5"
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-2 px-1">
            <input
              type="checkbox"
              checked={allPicked}
              onChange={(e) => setPicked(e.target.checked ? rows.map((r) => r.id) : [])}
              data-testid="mail-select-all"
              className="w-4 h-4 accent-paw-primary"
              aria-label="Select every message on this page"
            />
            {picked.length > 0 ? (
              <div className="flex items-center gap-1 flex-wrap">
                <button disabled={busy} onClick={() => runOnPicked("/admin/mail/flags", { read: "1" }, "Marked as read")} className={btn + " h-7 px-2 text-xs"} data-testid="mail-bulk-read">
                  <MailOpen className="w-3.5 h-3.5" /> Read
                </button>
                <button disabled={busy} onClick={() => runOnPicked("/admin/mail/flags", { read: "0" }, "Marked as unread")} className={btn + " h-7 px-2 text-xs"} data-testid="mail-bulk-unread">
                  <Mail className="w-3.5 h-3.5" /> Unread
                </button>
                <button disabled={busy} onClick={() => runOnPicked("/admin/mail/trash", {}, "Moved to Trash")} className={btn + " h-7 px-2 text-xs"} data-testid="mail-bulk-trash">
                  <Trash2 className="w-3.5 h-3.5" /> Trash
                </button>
              </div>
            ) : (
              <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">
                {loading ? "Loading" : total + (total === 1 ? " message" : " messages")}
              </span>
            )}
          </div>

          <div className="flex-1 overflow-y-auto max-h-[62vh] divide-y divide-paw-border dark:divide-white/5">
            {loading && rows.length === 0 && (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-paw-text-muted" /></div>
            )}
            {!loading && rows.length === 0 && (
              <p className="py-16 text-center font-manrope text-sm text-paw-text-muted dark:text-white/40" data-testid="mail-empty">
                {q ? "Nothing in this folder matches that search." : "Nothing in this folder."}
              </p>
            )}
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid="mail-row"
                className={cx(
                  "flex gap-2 px-2 py-2.5 cursor-pointer transition-colors",
                  openId === r.id ? "bg-paw-primary/[0.07] dark:bg-white/[0.06]" : "hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                )}
                onClick={() => openMessage(r, false)}
              >
                <input
                  type="checkbox"
                  checked={picked.includes(r.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))}
                  className="w-4 h-4 mt-1 accent-paw-primary shrink-0"
                  aria-label={"Select " + (r.subject || "message")}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); setOne(r, { starred: r.starred ? "0" : "1" }); }}
                  className="mt-0.5 shrink-0 p-0.5"
                  aria-label={r.starred ? "Remove star" : "Add star"}
                  data-testid="mail-star"
                >
                  <Star className={cx("w-4 h-4", r.starred ? "fill-paw-gold text-paw-gold" : "text-paw-text-muted/50 dark:text-white/25")} />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className={cx("truncate font-manrope text-sm", r.unread ? "font-bold" : "")}>
                      {folder === "sent" || folder === "drafts" ? (who(r.to) || "No recipient") : (who(r.from) || "Unknown sender")}
                    </span>
                    <span className="ml-auto shrink-0 font-manrope text-[11px] text-paw-text-muted dark:text-white/40">{fmtDate(r.ts)}</span>
                  </div>
                  <div className={cx("truncate font-manrope text-sm", r.unread ? "font-semibold" : "text-paw-text dark:text-white/80")}>
                    {r.subject || "(no subject)"}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="shrink-0 px-1.5 rounded font-manrope text-[10px] uppercase tracking-wide bg-black/[0.05] dark:bg-white/10 text-paw-text-muted dark:text-white/50" data-testid="mail-row-account">
                      {r.account_label}
                    </span>
                    {r.has_attachments && <Paperclip className="w-3 h-3 text-paw-text-muted dark:text-white/40 shrink-0" />}
                    <span className="truncate font-manrope text-[11px] text-paw-text-muted dark:text-white/40">{r.snippet}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-3 mt-1 border-t border-paw-border dark:border-white/5">
            <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Page {page} of {pages}</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} className={btn + " h-8 px-2"} data-testid="mail-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button disabled={page >= pages || loading} onClick={() => setPage(page + 1)} className={btn + " h-8 px-2"} data-testid="mail-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* reading pane */}
        <div className={cx(card, "p-4 sm:p-5 min-h-[300px] xl:col-span-1 lg:col-span-2 xl:col-auto")} data-testid="mail-reader">
          {!openId && (
            <div className="h-full min-h-[240px] flex flex-col items-center justify-center text-center">
              <Inbox className="w-8 h-8 text-paw-text-muted/40 dark:text-white/20 mb-3" />
              <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">Pick a message to read it here.</p>
            </div>
          )}

          {openId && fullLoading && (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-paw-text-muted" /></div>
          )}

          {openId && !fullLoading && full && (
            <div>
              <div className="flex items-start gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <h2 className="font-outfit font-bold text-lg sm:text-xl leading-snug break-words" data-testid="mail-subject">
                    {full.subject || "(no subject)"}
                  </h2>
                  <p className="font-manrope text-sm mt-1 break-words">{whoFull(full.from)}</p>
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 break-words">
                    to {whoFull(full.to) || "you"}{full.cc && full.cc.length ? ", cc " + whoFull(full.cc) : ""}
                  </p>
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1">
                    {fmtFull(full.ts)} · arrived at {full.account_address} · {fmtSize(full.size)}
                  </p>
                </div>
                <button onClick={() => { setOpenId(""); setFull(null); }} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close message" data-testid="mail-close">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap mb-4">
                {currentRow && currentRow.folder === "drafts" ? (
                  <button onClick={startEditDraft} className={btn} data-testid="mail-edit-draft">
                    <FileEdit className="w-4 h-4" /> Edit draft
                  </button>
                ) : (
                  <>
                    <button onClick={() => startReply(false)} className={btn} data-testid="mail-reply"><Reply className="w-4 h-4" /> Reply</button>
                    <button onClick={() => startReply(true)} className={btn} data-testid="mail-reply-all"><ReplyAll className="w-4 h-4" /> Reply all</button>
                    <button onClick={() => startForward()} className={btn} data-testid="mail-forward"><Forward className="w-4 h-4" /> Forward</button>
                  </>
                )}
                {currentRow && (
                  <button onClick={() => setOne(currentRow, { read: currentRow.unread ? "1" : "0" })} className={btn} data-testid="mail-toggle-read">
                    {currentRow.unread ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                    {currentRow.unread ? "Mark read" : "Mark unread"}
                  </button>
                )}
                {currentRow && currentRow.folder !== "trash" && (
                  <button disabled={busy} onClick={() => trashOne(currentRow)} className={btn} data-testid="mail-trash-one"><Trash2 className="w-4 h-4" /> Trash</button>
                )}
                {currentRow && (
                  <select
                    value=""
                    disabled={busy}
                    onChange={(e) => { if (e.target.value) moveOne(currentRow, e.target.value); }}
                    className="h-9 px-2 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 font-manrope text-sm outline-none"
                    data-testid="mail-move"
                    aria-label="Move this message to another folder"
                  >
                    <option value="">Move to</option>
                    {folders.filter((f) => f.key !== currentRow.folder).map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>
                )}
              </div>

              {full.blocked_images > 0 && !showImages && (
                <div className="flex items-center gap-3 flex-wrap mb-3 rounded-xl border border-paw-border dark:border-white/10 bg-black/[0.03] dark:bg-white/5 px-3 py-2">
                  <ImageOff className="w-4 h-4 text-paw-text-muted dark:text-white/40 shrink-0" />
                  <span className="font-manrope text-xs text-paw-text-muted dark:text-white/50 flex-1 min-w-[200px]">
                    {full.blocked_images} image{full.blocked_images === 1 ? " is" : "s are"} blocked. Loading them tells the sender this address is real and read.
                  </span>
                  <button onClick={() => currentRow && openMessage(currentRow, true)} className={btn + " h-7 px-2 text-xs"} data-testid="mail-show-images">
                    Show images
                  </button>
                </div>
              )}

              {(full.attachments || []).filter((a) => !a.inline).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="mail-attachments">
                  {(full.attachments || []).filter((a) => !a.inline).map((a) => (
                    <a
                      key={a.part}
                      href={"/api/admin/mail/attachment?account=" + encodeURIComponent(full.account) +
                        "&folder=" + encodeURIComponent(full.folder) +
                        "&uid=" + encodeURIComponent(full.uid) +
                        "&part=" + encodeURIComponent(a.part)}
                      className={btn + " max-w-full"}
                    >
                      <Download className="w-4 h-4 shrink-0" />
                      <span className="truncate">{a.filename}</span>
                      <span className="text-paw-text-muted dark:text-white/40 shrink-0">{fmtSize(a.size)}</span>
                    </a>
                  ))}
                </div>
              )}

              {/* The body runs inside a frame with scripts switched off. A hostile
                  email cannot reach the admin session from in there. */}
              <iframe
                title="Message body"
                data-testid="mail-body-frame"
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                srcDoc={frameDoc(full.html || (full.text ? "<pre>" + esc(full.text) + "</pre>" : ""))}
                className="w-full h-[55vh] min-h-[320px] rounded-xl border border-paw-border dark:border-white/10 bg-white"
              />
            </div>
          )}
        </div>
      </div>

      {/* compose */}
      {compose && (
        <div className="fixed inset-0 z-[130] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="mail-compose">
          <div className="w-full sm:max-w-3xl max-h-[94vh] overflow-y-auto bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-t-2xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit font-bold text-lg">
                {compose.mode === "new" ? "New email" : compose.mode === "forward" ? "Forward" : "Reply"}
              </h2>
              <button onClick={() => setCompose(null)} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close" data-testid="mail-compose-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">From</label>
                <select
                  value={compose.account}
                  onChange={(e) => setCompose({ ...compose, account: e.target.value })}
                  className={field}
                  data-testid="mail-compose-from"
                >
                  {accounts.map((a) => (
                    <option key={a.key} value={a.key}>{a.address}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">To</label>
                <input value={compose.to} onChange={(e) => setCompose({ ...compose, to: e.target.value })} className={field} placeholder="name@example.com" data-testid="mail-compose-to" />
              </div>

              {!compose.showCc && (
                <button onClick={() => setCompose({ ...compose, showCc: true })} className="font-manrope text-xs text-paw-primary" data-testid="mail-compose-showcc">
                  Add Cc and Bcc
                </button>
              )}
              {compose.showCc && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">Cc</label>
                    <input value={compose.cc} onChange={(e) => setCompose({ ...compose, cc: e.target.value })} className={field} data-testid="mail-compose-cc" />
                  </div>
                  <div>
                    <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">Bcc</label>
                    <input value={compose.bcc} onChange={(e) => setCompose({ ...compose, bcc: e.target.value })} className={field} data-testid="mail-compose-bcc" />
                  </div>
                </div>
              )}

              <div>
                <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">Subject</label>
                <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} className={field} data-testid="mail-compose-subject" />
              </div>

              <div>
                <label className="block font-manrope text-xs text-paw-text-muted dark:text-white/40 mb-1">Message</label>
                <textarea
                  value={compose.body}
                  onChange={(e) => setCompose({ ...compose, body: e.target.value })}
                  rows={10}
                  data-testid="mail-compose-body"
                  className="w-full p-3 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 font-manrope text-sm outline-none focus:border-paw-primary resize-y"
                  placeholder="Write your message. Just type normally, the PawLinx layout is added when it sends."
                />
                <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1.5" data-testid="mail-compose-note">
                  {compose.mode === "new"
                    ? "This goes out on the full PawLinx template, logo and footer included."
                    : "A reply carries the short PawLinx sign off and quotes the message below it, so the logo is not repeated down the thread."}
                </p>
              </div>

              <div>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  onChange={(e) => setCompose({ ...compose, files: Array.from(e.target.files || []) })}
                  className="hidden"
                  data-testid="mail-compose-files"
                />
                <button onClick={() => fileRef.current && fileRef.current.click()} className={btn} data-testid="mail-compose-attach">
                  <Paperclip className="w-4 h-4" /> Attach files
                </button>
                {(compose.files || []).length > 0 && (
                  <p className="font-manrope text-xs text-paw-text-muted dark:text-white/50 mt-2">
                    {compose.files.map((f) => f.name + " (" + fmtSize(f.size) + ")").join(", ")}
                  </p>
                )}
                {compose.mode === "forward" && (
                  <label className="flex items-center gap-2 mt-3 font-manrope text-xs text-paw-text-muted dark:text-white/50">
                    <input
                      type="checkbox"
                      checked={!!compose.keepAttachments}
                      onChange={(e) => setCompose({ ...compose, keepAttachments: e.target.checked })}
                      className="w-4 h-4 accent-paw-primary"
                      data-testid="mail-compose-keepattach"
                    />
                    Send the original attachments along
                  </label>
                )}
              </div>

              {compose.mode !== "new" && (
                <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 flex items-center gap-1.5">
                  <CornerUpLeft className="w-3.5 h-3.5" /> The message you are answering is quoted underneath automatically.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2 mt-5 flex-wrap">
              <button
                onClick={doSend}
                disabled={sending}
                className="flex items-center gap-2 px-5 h-10 rounded-lg bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope text-sm font-semibold disabled:opacity-50 transition-colors"
                data-testid="mail-compose-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
              </button>
              <button onClick={doDraft} disabled={sending} className={btn + " h-10 px-4"} data-testid="mail-compose-draft">
                <FileEdit className="w-4 h-4" /> Save draft
              </button>
              <button onClick={() => setCompose(null)} disabled={sending} className={btn + " h-10 px-4"} data-testid="mail-compose-cancel">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
